import React, { useMemo, useState, ReactNode, useCallback } from 'react';
import { Stack, Box } from '@mui/material';
import { BestPractice } from './types';
import EditableBestPracticeCard from './EditableBestPracticeCard';
import SelectableBestPracticeCard from './SelectableBestPracticeCard';
import { SearchInput } from '../common/SearchInput';
import useSortWidget from '../common/useSortWidget';
import { colors, Typography, Button, Switch } from '@sweep-io/sweep-design';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';
import useAiAgents from '../aiAgentCenter/useAiAgents';

interface BestPracticesListProps {
  bestPractices: BestPractice[];
  isCustom: boolean;
  onIsPristineChange: (cardId: string, newIsPristine: boolean) => void;
  children: ReactNode;
  button?: ReactNode;
  agent?: AiAgent | null;
  isAgentCreationContext?: boolean;
  selectedBestPracticeIds?: string[];
  onBestPracticesChange?: (bestPracticeIds: string[]) => void;
}

const NAME_SORT = 'Name';
const DATE_MODIFIED_SORT = 'Date Modified';

const sortOptions = [NAME_SORT, DATE_MODIFIED_SORT];

const BestPracticesList: React.FC<BestPracticesListProps> = ({
  bestPractices,
  isCustom,
  onIsPristineChange,
  children,
  button,
  agent,
  isAgentCreationContext = false,
  selectedBestPracticeIds = [],
  onBestPracticesChange,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const { addAgentBestPractices, removeAgentBestPractices } = useAiAgents();

  const { SortWidget, sortBy } = useSortWidget({
    defaultValue: DATE_MODIFIED_SORT,
    sortOptions,
    size: 'small',
  });

  const filteredAndSortedBestPractices = useMemo(() => {
    const filteredList = bestPractices.filter((bestPractice) => {
      if (!searchValue.trim()) return true;

      const searchLower = searchValue.toLowerCase();
      return (
        bestPractice.name.toLowerCase().includes(searchLower) ||
        bestPractice.text.toLowerCase().includes(searchLower) ||
        bestPractice.applicableResourceType.toLowerCase().includes(searchLower)
      );
    });

    const sortedList = [...filteredList].sort((a, b) => {
      if (sortBy === NAME_SORT) {
        return a.name.localeCompare(b.name);
      } else if (sortBy === DATE_MODIFIED_SORT) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      return 0;
    });

    return sortedList;
  }, [bestPractices, searchValue, sortBy]);

  const clearFilters = () => {
    setSearchValue('');
  };

  const noResults = filteredAndSortedBestPractices.length === 0 && searchValue.trim();

  const agentSelectedBestPracticeIds = useMemo(
    () => agent?.bestPractices?.map((bp) => bp.bestPracticeId) ?? [],
    [agent?.bestPractices],
  );

  const showToggle = isAgentCreationContext ? !!onBestPracticesChange : !!agent;

  const currentTabBestPracticeIds = useMemo(
    () => bestPractices.map((bp) => bp.id),
    [bestPractices],
  );

  // Check if all items in the current tab are selected.
  // In creation context: check against local state (selectedBestPracticeIds)
  // In settings context: check against agent's existing data (agentSelectedBestPracticeIds)
  const isAllCurrentTabSelected = useMemo(() => {
    if (bestPractices.length === 0) return false;

    if (isAgentCreationContext) {
      return currentTabBestPracticeIds.every((id) => selectedBestPracticeIds.includes(id));
    } else {
      return currentTabBestPracticeIds.every((id) => agentSelectedBestPracticeIds.includes(id));
    }
  }, [
    isAgentCreationContext,
    currentTabBestPracticeIds,
    selectedBestPracticeIds,
    agentSelectedBestPracticeIds,
    bestPractices.length,
  ]);

  const handleToggleAll = useCallback(
    async (checked: boolean) => {
      if (isAgentCreationContext && onBestPracticesChange) {
        // Creation context: update local state
        if (checked) {
          const newSelection = [
            ...new Set([...selectedBestPracticeIds, ...currentTabBestPracticeIds]),
          ];
          onBestPracticesChange(newSelection);
        } else {
          const newSelection = selectedBestPracticeIds.filter(
            (id) => !currentTabBestPracticeIds.includes(id),
          );
          onBestPracticesChange(newSelection);
        }
      } else if (agent) {
        // Settings context: make API calls
        if (checked) {
          const idsToAdd = currentTabBestPracticeIds.filter(
            (id) => !agentSelectedBestPracticeIds.includes(id),
          );
          if (idsToAdd.length > 0) {
            await addAgentBestPractices(agent.id, idsToAdd);
          }
        } else {
          const idsToRemove = currentTabBestPracticeIds.filter((id) =>
            agentSelectedBestPracticeIds.includes(id),
          );
          if (idsToRemove.length > 0) {
            await removeAgentBestPractices(agent.id, idsToRemove);
          }
        }
      }
    },
    [
      isAgentCreationContext,
      onBestPracticesChange,
      selectedBestPracticeIds,
      currentTabBestPracticeIds,
      agent,
      agentSelectedBestPracticeIds,
      addAgentBestPractices,
      removeAgentBestPractices,
    ],
  );

  return (
    <Stack gap={2} position="relative">
      <Stack
        sx={{
          backgroundColor: colors.white,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack
          direction="row"
          sx={{
            borderRadius: '8px',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: 1,
            }}
          >
            <Box sx={{ flex: 1, maxWidth: '269px' }}>
              <SearchInput
                withFixedMagnifyingGlassIcon
                placeholder="Search"
                TextFieldProps={{
                  value: searchValue,
                  onChange: (event) => setSearchValue(event.target.value),
                }}
                onClearButtonClick={() => setSearchValue('')}
                size="small"
              />
            </Box>
            {SortWidget}
          </Box>
          {button ? button : null}
        </Stack>

        {showToggle && bestPractices.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} mt={2}>
            <Typography variant="caption" color={colors.grey[700]}>
              Switch on all
            </Typography>
            <Switch
              checked={isAllCurrentTabSelected}
              onChange={(e) => handleToggleAll(e.target.checked)}
              size="small"
            />
          </Stack>
        )}
      </Stack>

      {children}

      {noResults && (
        <Stack gap={1} p={3} alignItems="center">
          <Typography variant="h4" color={colors.grey[700]}>
            No results found for "{searchValue}"
          </Typography>
          <Button size="medium" variant="flat-blue" onClick={clearFilters}>
            Clear search
          </Button>
        </Stack>
      )}
      <Stack gap={2}>
        {filteredAndSortedBestPractices.map((bestPractice, index) =>
          isAgentCreationContext && onBestPracticesChange ? (
            <SelectableBestPracticeCard
              key={bestPractice.id}
              bestPractice={bestPractice}
              isCustom={isCustom}
              index={index}
              agent={agent}
              onIsPristineChange={(newIsPristine) =>
                onIsPristineChange(bestPractice.id, newIsPristine)
              }
              selectedBestPracticeIds={selectedBestPracticeIds}
              onBestPracticesChange={onBestPracticesChange}
            />
          ) : (
            <EditableBestPracticeCard
              key={bestPractice.id}
              bestPractice={bestPractice}
              isCustom={isCustom}
              index={index}
              agent={agent}
              onIsPristineChange={(newIsPristine) =>
                onIsPristineChange(bestPractice.id, newIsPristine)
              }
            />
          ),
        )}
      </Stack>
    </Stack>
  );
};

export default BestPracticesList;
